import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { InfiniteData, QueryKey, useQueryClient } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import { DocumentRow } from '@/types/documents'
import { ProjectGridField } from '@/types/fields'
import PaginatedResponse from '@/types/paginated-response'
import useSubscribe from '@/components/notifications/useSubscribe'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import { useSelectedWorkflowStateContext } from '@/components/workflows/SelectedWorkflowStateProvider'
import BulkEditValueDialog from './BulkEditValueDialog'
import ClearFieldValuesDialog from './ClearFieldValuesDialog'
import RemoveManualValuesDialog from './RemoveManualValuesDialog'

type BulkActionType = 'edit' | 'clear' | 'removeManual'

type BulkAction = {
  type: BulkActionType
  field: ProjectGridField
  selectedRows: Required<DocumentRow>[]
}

type BulkEditValueContextValue = {
  openBulkEdit: (
    field: ProjectGridField,
    selectedRows: Required<DocumentRow>[],
  ) => void
  openClearConfirm: (
    field: ProjectGridField,
    selectedRows: Required<DocumentRow>[],
  ) => void
  openRemoveManualConfirm: (
    field: ProjectGridField,
    selectedRows: Required<DocumentRow>[],
  ) => void
  setPendingEdits: (edits: Map<string, Set<string>>) => void
  isCellPending: (rowId: string, fieldId: string) => boolean
  getRowsFromCache: (queryKey: QueryKey) => Required<DocumentRow>[]
  fetchAllMatchingRows: (field: ProjectGridField) => Promise<{
    allRows: Required<DocumentRow>[]
    allMatchingRows: Required<DocumentRow>[]
  }>
  pendingFieldId: string | null
  listQueryKey: QueryKey | undefined
  fetchAllPages: (() => Promise<void>) | undefined
  selectedAllDocsCount: number | undefined
}

const BulkEditValueContext = createContext<BulkEditValueContextValue>(
  {} as BulkEditValueContextValue,
)

export const useBulkEditValueContext = () => useContext(BulkEditValueContext)

type BulkEditValueProviderProps = {
  children: ReactNode
  listQueryKey: QueryKey
  fetchAllPages: (() => Promise<void>) | undefined
  selectedAllDocsCount: number | undefined
}

export default function BulkEditValueProvider({
  children,
  listQueryKey,
  fetchAllPages,
  selectedAllDocsCount,
}: BulkEditValueProviderProps) {
  const overlay = useOverlay()
  const queryClient = useQueryClient()
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const { selectedWorkflowState } = useSelectedWorkflowStateContext()

  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null)

  // Track pending state with refs for the hot path (isCellPending reads these
  // without causing context changes) and parallel state for reactivity where needed.
  // pendingRowIdsRef: flat set of row IDs showing skeletons.
  // pendingDocIdsRef: set of doc IDs for matching WebSocket notifications.
  // pendingDocIds state: drives useSubscribe body (needs reactivity).
  const pendingRowIdsRef = useRef<Set<string>>(new Set())
  const pendingDocIdsRef = useRef<Set<string>>(new Set())
  const pendingFieldIdRef = useRef<string | null>(null)
  const [pendingFieldId, setPendingFieldId] = useState<string | null>(null)
  const [pendingDocIds, setPendingDocIdsState] = useState<Set<string>>(
    new Set(),
  )

  // Track completed doc notifications. Skeletons stay on ALL pending cells
  // until every doc has finished AND fresh data has been fetched.
  const completedCountRef = useRef(0)

  const setPendingEdits = useCallback(
    (edits: Map<string, Set<string>>) => {
      const allRowIds = new Set<string>()
      for (const rowIds of edits.values()) {
        for (const rowId of rowIds) {
          allRowIds.add(rowId)
        }
      }
      pendingRowIdsRef.current = allRowIds
      pendingDocIdsRef.current = new Set(edits.keys())
      completedCountRef.current = 0
      const fieldId = bulkAction?.field.id ?? null
      pendingFieldIdRef.current = fieldId
      setPendingFieldId(fieldId)
      setPendingDocIdsState(new Set(edits.keys()))
    },
    [bulkAction?.field.id],
  )

  // Stable check for whether a cell is pending a bulk edit result.
  // Reads from refs so it never causes context value changes.
  const isCellPending = useCallback(
    (rowId: string, fieldId: string) => {
      if (pendingFieldIdRef.current !== fieldId) return false
      return pendingRowIdsRef.current.has(rowId)
    },
    [], // stable — reads from refs
  )

  const getRowsFromCache = useCallback(
    (queryKey: QueryKey) => {
      const cached =
        queryClient.getQueryData<InfiniteData<PaginatedResponse<DocumentRow>>>(
          queryKey,
        )
      return (cached?.pages.flatMap((p) => p.results) ??
        []) as Required<DocumentRow>[]
    },
    [queryClient],
  )

  const fetchAllMatchingRows = useCallback(
    async (field: ProjectGridField) => {
      if (fetchAllPages) await fetchAllPages()
      const allRows = listQueryKey ? getRowsFromCache(listQueryKey) : []
      const allMatchingRows = allRows.filter(
        (row) => row.project_grid_id === field.project_grid_id,
      )
      return { allRows, allMatchingRows }
    },
    [fetchAllPages, getRowsFromCache, listQueryKey],
  )

  // Subscribe to doc_changes notifications for pending document IDs
  const subscribeBody = useMemo(() => {
    if (!pendingDocIds.size) return undefined
    return [
      {
        endpoint_path: '/v2/pd/documents',
        obj_ids: [...pendingDocIds],
      },
    ]
  }, [pendingDocIds])

  useSubscribe({
    onMessage: useCallback(
      async (event: MessageEvent) => {
        const data = JSON.parse(event.data)
        if (data.action !== '/v2/pd-rules/doc_changes|new') return
        const docId: string | undefined =
          data.obj_id ?? data.stripped_partial_msg?.obj_id
        if (!docId || !pendingDocIdsRef.current.has(docId)) return

        completedCountRef.current++

        // All docs done — refetch fresh data, THEN clear pending state
        if (completedCountRef.current >= pendingDocIdsRef.current.size) {
          completedCountRef.current = 0
          await queryClient.refetchQueries({ queryKey: listQueryKey })
          pendingRowIdsRef.current = new Set()
          pendingDocIdsRef.current = new Set()
          pendingFieldIdRef.current = null
          setPendingFieldId(null)
          setPendingDocIdsState(new Set())
        }
      },
      [listQueryKey, queryClient],
    ),
    body: subscribeBody,
  })

  const openBulkEdit = useCallback(
    (field: ProjectGridField, selectedRows: Required<DocumentRow>[]) => {
      setBulkAction({ type: 'edit', field, selectedRows })
      overlay.open()
    },
    [overlay],
  )

  const openClearConfirm = useCallback(
    (field: ProjectGridField, selectedRows: Required<DocumentRow>[]) => {
      setBulkAction({ type: 'clear', field, selectedRows })
      overlay.open()
    },
    [overlay],
  )

  const openRemoveManualConfirm = useCallback(
    (field: ProjectGridField, selectedRows: Required<DocumentRow>[]) => {
      setBulkAction({ type: 'removeManual', field, selectedRows })
      overlay.open()
    },
    [overlay],
  )

  const contextValue = useMemo(
    () => ({
      openBulkEdit,
      openClearConfirm,
      openRemoveManualConfirm,
      setPendingEdits,
      isCellPending,
      getRowsFromCache,
      fetchAllMatchingRows,
      pendingFieldId,
      listQueryKey,
      fetchAllPages,
      selectedAllDocsCount,
    }),
    [
      openBulkEdit,
      openClearConfirm,
      openRemoveManualConfirm,
      setPendingEdits,
      isCellPending,
      getRowsFromCache,
      fetchAllMatchingRows,
      pendingFieldId,
      listQueryKey,
      fetchAllPages,
      selectedAllDocsCount,
    ],
  )

  const commonProps = {
    overlay,
    field: bulkAction?.field as ProjectGridField,
    selectedRows: bulkAction?.selectedRows as Required<DocumentRow>[],
    workflowId: selectedWorkflow.id,
    workflowStateId: selectedWorkflowState?.id || '',
    listQueryKey,
    setPendingEdits,
  }

  return (
    <BulkEditValueContext.Provider value={contextValue}>
      {children}
      {bulkAction?.type === 'edit' && <BulkEditValueDialog {...commonProps} />}
      {bulkAction?.type === 'clear' && (
        <ClearFieldValuesDialog {...commonProps} />
      )}
      {bulkAction?.type === 'removeManual' && (
        <RemoveManualValuesDialog {...commonProps} />
      )}
    </BulkEditValueContext.Provider>
  )
}
